import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  TrendingUp,
  Users,
  Target,
  Download,
  FileSpreadsheet,
  Award,
  Calendar,
  BarChart3,
  Percent
} from 'lucide-react';
import { exportToCSV, exportToExcel, formatDateForExport, formatSubscriptionTier } from '@/utils/exportUtils';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { format, subDays, eachDayOfInterval, parseISO, startOfDay } from 'date-fns';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';

interface CampaignDashboardProps {
  campaignName: string;
  codes: any[];
}

export const CampaignDashboard: React.FC<CampaignDashboardProps> = ({ campaignName, codes }) => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  // Get all code IDs for this campaign
  const campaignCodeIds = codes.map(c => c.id);

  // Fetch all redemptions for campaign codes with user and payment data
  const { data: allRedemptions, isLoading: redemptionsLoading } = useQuery({
    queryKey: ['campaign-redemptions', campaignName, campaignCodeIds],
    queryFn: async () => {
      if (campaignCodeIds.length === 0) return [];

      const { data: redemptions, error } = await supabase
        .from('promotional_code_redemptions')
        .select('*, users:user_id(id, email, full_name, subscription_tier)')
        .in('code_id', campaignCodeIds)
        .order('redeemed_at', { ascending: false });

      if (error) throw error;
      if (!redemptions || redemptions.length === 0) return [];

      // Fetch billing cycle for each user from payments table
      const userIds = [...new Set(redemptions.map((r: any) => r.users?.id).filter(Boolean))];
      
      let userBillingCycles: Record<string, string> = {};
      if (userIds.length > 0) {
        const { data: payments } = await supabase
          .from('payments')
          .select('user_id, billing_cycle')
          .in('user_id', userIds)
          .eq('status', 'completed')
          .order('created_at', { ascending: false });
        
        // Get the most recent billing cycle for each user
        if (payments) {
          payments.forEach((p: any) => {
            if (!userBillingCycles[p.user_id]) {
              userBillingCycles[p.user_id] = p.billing_cycle;
            }
          });
        }
      }

      // Enrich redemptions with billing cycle
      return redemptions.map((r: any) => ({
        ...r,
        billing_cycle: r.users?.id ? userBillingCycles[r.users.id] || null : null
      }));
    },
    enabled: campaignCodeIds.length > 0
  });

  // Calculate campaign statistics
  const campaignStats = useMemo(() => {
    if (!allRedemptions) return null;

    const totalRedemptions = allRedemptions.length;
    const totalMaxUses = codes.reduce((sum, c) => sum + (c.max_uses || 0), 0);
    const conversionRate = totalMaxUses > 0 ? ((totalRedemptions / totalMaxUses) * 100).toFixed(1) : 'N/A';

    // Find most popular code
    const codeRedemptionCounts: Record<string, number> = {};
    allRedemptions.forEach((r: any) => {
      codeRedemptionCounts[r.code_id] = (codeRedemptionCounts[r.code_id] || 0) + 1;
    });

    let mostPopularCode = null;
    let maxRedemptions = 0;
    Object.entries(codeRedemptionCounts).forEach(([codeId, count]) => {
      if (count > maxRedemptions) {
        maxRedemptions = count;
        const code = codes.find(c => c.id === codeId);
        mostPopularCode = code?.code || 'Unknown';
      }
    });

    // Plan distribution
    const planDistribution: Record<string, number> = {};
    allRedemptions.forEach((r: any) => {
      const tier = r.users?.subscription_tier || 'free';
      planDistribution[tier] = (planDistribution[tier] || 0) + 1;
    });

    return {
      totalCodes: codes.length,
      totalRedemptions,
      totalMaxUses,
      conversionRate,
      mostPopularCode,
      mostPopularCodeRedemptions: maxRedemptions,
      planDistribution
    };
  }, [allRedemptions, codes]);

  // Calculate chart data for temporal evolution
  const chartData = useMemo(() => {
    if (!allRedemptions || allRedemptions.length === 0) return [];

    const now = new Date();
    let startDate: Date;

    switch (dateRange) {
      case '7d':
        startDate = subDays(now, 7);
        break;
      case '30d':
        startDate = subDays(now, 30);
        break;
      case '90d':
        startDate = subDays(now, 90);
        break;
      case 'all':
      default:
        const dates = allRedemptions.map((r: any) => new Date(r.redeemed_at));
        startDate = dates.length > 0 ? new Date(Math.min(...dates.map(d => d.getTime()))) : subDays(now, 30);
        break;
    }

    const days = eachDayOfInterval({ start: startDate, end: now });

    const redemptionsByDay: Record<string, number> = {};
    allRedemptions.forEach((r: any) => {
      const date = format(startOfDay(new Date(r.redeemed_at)), 'yyyy-MM-dd');
      redemptionsByDay[date] = (redemptionsByDay[date] || 0) + 1;
    });

    let cumulative = 0;
    return days.map(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const count = redemptionsByDay[dateStr] || 0;
      cumulative += count;
      return {
        date: format(day, 'dd/MM'),
        fullDate: dateStr,
        redemptions: count,
        cumulative
      };
    });
  }, [allRedemptions, dateRange]);

  // Export consolidated campaign report
  const handleExportCampaignReport = async (type: 'csv' | 'excel') => {
    if (!allRedemptions || allRedemptions.length === 0) {
      toast.error(t('promoCodes.campaign.noDataToExport'));
      return;
    }

    // Build enriched data with code info
    const exportData = allRedemptions.map((r: any) => {
      const code = codes.find(c => c.id === r.code_id);
      return {
        code: code?.code || 'Unknown',
        campaign: campaignName,
        user_name: r.users?.full_name || t('promoCodes.unknownUser'),
        user_email: r.users?.email || '',
        subscription_tier: r.users?.subscription_tier || 'free',
        billing_cycle: r.billing_cycle || '',
        redeemed_at: r.redeemed_at,
        reward_type: r.reward_granted?.type || '',
        reward_value: r.reward_granted?.amount || r.reward_granted?.percentage || r.reward_granted?.description || ''
      };
    });

    const formatBillingCycle = (val: string) => {
      if (!val) return t('promoCodes.campaign.noBillingCycle');
      if (val === 'monthly') return t('promoCodes.campaign.monthly');
      if (val === 'yearly' || val === 'annual') return t('promoCodes.campaign.yearly');
      return val;
    };

    const columns = [
      { key: 'code', header: t('promoCodes.code') },
      { key: 'campaign', header: t('promoCodes.campaignLabel') },
      { key: 'user_name', header: t('promoCodes.user') },
      { key: 'user_email', header: t('promoCodes.email') },
      {
        key: 'subscription_tier',
        header: t('promoCodes.subscriptionPlan'),
        formatter: (val: string) => formatSubscriptionTier(val)
      },
      {
        key: 'billing_cycle',
        header: t('promoCodes.campaign.billingCycle'),
        formatter: (val: string) => formatBillingCycle(val)
      },
      {
        key: 'redeemed_at',
        header: t('promoCodes.redeemedAt'),
        formatter: (val: string) => formatDateForExport(val)
      },
      { key: 'reward_type', header: t('promoCodes.campaign.rewardType') },
      { key: 'reward_value', header: t('promoCodes.campaign.rewardValue') }
    ];

    const filename = `campaign_${campaignName.replace(/\s+/g, '_')}_report`;

    if (type === 'csv') {
      exportToCSV(exportData, columns, filename);
    } else {
      await exportToExcel(exportData, columns, filename, t('promoCodes.campaign.reportSheet'));
    }

    toast.success(t('promoCodes.exportSuccess'));
  };

  if (redemptionsLoading) {
    return <Skeleton className="h-96" />;
  }

  return (
    <div className="space-y-4">
      {/* Campaign Header with Export Buttons */}
      <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-xl flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                {t('promoCodes.campaign.dashboardTitle')}: {campaignName}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {t('promoCodes.campaign.dashboardSubtitle')}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleExportCampaignReport('csv')}>
                <Download className="h-4 w-4 mr-1" />
                CSV
              </Button>
              <Button variant="default" size="sm" onClick={() => handleExportCampaignReport('excel')}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />
                Excel
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Campaign Stats Cards */}
      {campaignStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-primary" />
                <span className="text-xs text-muted-foreground">{t('promoCodes.totalCodes')}</span>
              </div>
              <p className="text-2xl font-bold mt-1">{campaignStats.totalCodes}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-green-500" />
                <span className="text-xs text-muted-foreground">{t('promoCodes.totalRedemptions')}</span>
              </div>
              <p className="text-2xl font-bold mt-1">{campaignStats.totalRedemptions}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <Percent className="h-4 w-4 text-blue-500" />
                <span className="text-xs text-muted-foreground">{t('promoCodes.campaign.conversionRate')}</span>
              </div>
              <p className="text-2xl font-bold mt-1">{campaignStats.conversionRate}%</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4 text-yellow-500" />
                <span className="text-xs text-muted-foreground">{t('promoCodes.campaign.topCode')}</span>
              </div>
              <p className="text-lg font-bold mt-1 font-mono truncate">
                {campaignStats.mostPopularCode || '-'}
              </p>
              {campaignStats.mostPopularCode && (
                <p className="text-xs text-muted-foreground">
                  {campaignStats.mostPopularCodeRedemptions} {t('promoCodes.redemptions').toLowerCase()}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Plan Distribution */}
      {campaignStats && Object.keys(campaignStats.planDistribution).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              {t('promoCodes.campaign.planDistribution')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(campaignStats.planDistribution).map(([plan, count]) => (
                <Badge key={plan} variant="secondary" className="text-sm">
                  {formatSubscriptionTier(plan)}: {count} ({((count / campaignStats.totalRedemptions) * 100).toFixed(0)}%)
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Temporal Evolution Chart */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              {t('promoCodes.campaign.temporalEvolution')}
            </CardTitle>
            <Select value={dateRange} onValueChange={(v) => setDateRange(v as any)}>
              <SelectTrigger className="w-[140px]">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">{t('promoCodes.campaign.last7Days')}</SelectItem>
                <SelectItem value="30d">{t('promoCodes.campaign.last30Days')}</SelectItem>
                <SelectItem value="90d">{t('promoCodes.campaign.last90Days')}</SelectItem>
                <SelectItem value="all">{t('promoCodes.campaign.allTime')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorRedemptions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="redemptions"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    fill="url(#colorRedemptions)"
                    name={t('promoCodes.campaign.dailyRedemptions')}
                  />
                  <Line
                    type="monotone"
                    dataKey="cumulative"
                    stroke="hsl(var(--chart-2))"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    name={t('promoCodes.campaign.cumulative')}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              <p>{t('promoCodes.campaign.noChartData')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
